"use strict";
/*
 * Membrane React package
 * {@link https://www.npmjs.com/package/@membranehq/react}
 * v0.29.2
 */
'use client';

import { jsx, jsxs } from 'react/jsx-runtime';
import { DropdownUI } from '../../DropdownUI/index.mjs';
import { DROPDOWN_NAVIGATOR_ID } from '../../DropdownUI/constants.mjs';
import { cn } from '../../../helpers/cn.mjs';
import { useOptionListKeyNavDispatch } from '../../../helpers/hooks/keyboard-events.mjs';
import { useWindowEvent } from '../../../helpers/hooks/useWindowEvent.mjs';
import { useId, useRef } from 'react';
import { Root, Anchor, Content as Content$1, Title } from '../../../ui-kit/styled/popover.mjs';
import { BoundedPortal } from '../../FloatingPortalBoundary/index.mjs';
import { useComboBox } from '../context/use-combobox.mjs';
import { useDropdownStackContext } from '../context/use-dropdown-stack.mjs';
import { DropdownFocusTrap } from '../FocusTrap/index.mjs';
import { DropdownPositioner } from './DropdownPositioner.mjs';

const GUTTER = 10;
const POSITIONING = {
  placement: "bottom-start",
  gutter: GUTTER,
  slide: true,
  fitViewport: true
};
function DropdownPopoverProvider({ children, disabled }) {
  const { isDropdownOpen, closeDropdown } = useComboBox();
  return /* @__PURE__ */ jsx(
    Root,
    {
      open: disabled ? false : isDropdownOpen,
      onEscapeKeyDown: closeDropdown,
      closeOnInteractOutside: false,
      unmountOnExit: true,
      lazyMount: true,
      portalled: true,
      autoFocus: false,
      positioning: POSITIONING,
      children: /* @__PURE__ */ jsx(
        DropdownFocusTrap,
        {
          disabled,
          className: cn(
            "w-full not-in-contenteditable:flex not-in-contenteditable:items-center",
            "[[data-formula='condition']_&]:p-1.5 [[data-formula='condition']_&]:!items-start"
          ),
          children
        }
      )
    }
  );
}
function DropdownPopoverContent({ children }) {
  return /* @__PURE__ */ jsx(BoundedPortal, { children: /* @__PURE__ */ jsx(DropdownPositioner, { children: /* @__PURE__ */ jsx(DropdownFocusTrap, { children: /* @__PURE__ */ jsx(Content, { children }) }) }) });
}
function DropdownAnchor({ children }) {
  return /* @__PURE__ */ jsx(Anchor, { className: "inline max-w-full not-in-contenteditable:inline-flex not-in-contenteditable:items-center", children });
}
function Content({ children }) {
  const id = useId();
  const ref = useRef(null);
  const { scopedComboboxId, closeDropdown } = useComboBox();
  useOptionListKeyNavDispatch(ref.current, scopedComboboxId);
  const { isClickWithinStackItem } = useDropdownStackContext(ref);
  useClick((event) => {
    if (!isClickWithinStackItem(event)) {
      closeDropdown();
    }
  });
  return /* @__PURE__ */ jsx(Content$1, { ref, "data-navigation-emitter-id": DROPDOWN_NAVIGATOR_ID, asChild: true, "aria-labelledby": id, children: /* @__PURE__ */ jsx(DropdownUI, { children: /* @__PURE__ */ jsxs(DropdownUI.Content, { children: [
    /* @__PURE__ */ jsx(Title, { className: "sr-only", id, children: "Options menu" }),
    children
  ] }) }) });
}
function useClick(cb) {
  useWindowEvent("mouseup", cb);
  useWindowEvent("touchend", cb);
}

export { DropdownAnchor, DropdownPopoverContent, DropdownPopoverProvider };
